/**
 * Card caption text (card contract in styles/cards.css): at most two lines in the caption band under the picture. A title that does not fit
 * first shrinks one step, then ends with an ellipsis — and then the whole title shows as a tooltip (hover / focus via
 * `title`) and as a small bubble on a long press (touch), without opening the card.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type MouseEvent } from 'react';

const LONG_PRESS_MS = 450;

export function CardTitle({ text, className = '' }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [fit, setFit] = useState<0 | 1>(0);
  const [cut, setCut] = useState(false);
  const [tip, setTip] = useState(false);
  const timer = useRef(0);
  const pressed = useRef(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || !el.clientHeight) return;
    const over = el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
    if (over && fit === 0) setFit(1);
    else setCut(over);
  }, [fit]);

  useLayoutEffect(measure, [measure, text]);
  useEffect(() => {
    setFit(0);
    setCut(false);
  }, [text]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (!tip) return;
    const t = window.setTimeout(() => setTip(false), 2200);
    return () => window.clearTimeout(t);
  }, [tip]);

  const down = (e: PointerEvent) => {
    if (!cut || e.pointerType === 'mouse') return;
    pressed.current = false;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      pressed.current = true;
      setTip(true);
    }, LONG_PRESS_MS);
  };
  const up = () => window.clearTimeout(timer.current);
  // A long press shows the title; it must not also open the card.
  const click = (e: MouseEvent) => {
    if (!pressed.current) return;
    pressed.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <span className={`card-title-wrap ${className}`}>
      <span
        ref={ref}
        className={`card-title${fit ? ' fit-sm' : ''}`}
        title={cut ? text : undefined}
        data-truncated={cut ? 'true' : undefined}
        onPointerDown={down}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        onClickCapture={click}
        onContextMenu={(e) => cut && e.preventDefault()}
      >
        {text}
      </span>
      {tip && (
        <span className="card-tip" role="tooltip" data-testid="card-tip">
          {text}
        </span>
      )}
    </span>
  );
}
